/**
 * @fileoverview Spanish translations.
 * @summary Spanish translation strings for KaraoNow frontend.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n/locales/es
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
export const es = {
  common: {
    loading: 'Cargando...',
    processing: 'Procesando',
    error: 'Error',
    success: 'Éxito',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Guardar',
    delete: 'Eliminar',
    edit: 'Editar',
    create: 'Crear',
    back: 'Volver',
    next: 'Siguiente',
    previous: 'Anterior',
    search: 'Buscar',
    filter: 'Filtrar',
    close: 'Cerrar',
    backHome: 'Volver al inicio',
    creating: 'Creando...',
  },
  auth: {
    login: 'Iniciar sesión',
    register: 'Registrarse',
    logout: 'Cerrar sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    confirmPassword: 'Confirmar contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    rememberMe: 'Recordarme',
    loginButton: 'Iniciar sesión',
    registerButton: 'Registrarse',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
    dontHaveAccount: '¿No tienes una cuenta?',
    role: 'Rol',
    organizer: 'Organizador',
    singer: 'Cantante',
    firstName: 'Nombre',
    lastName: 'Apellido',
    pseudo: 'Pseudo',
    organization: 'Organización',
    sendVerificationEmail: 'Enviar email de verificación',
    emailVerificationSent: 'Email de verificación enviado',
    emailVerificationInstructions: 'Se ha enviado un email de verificación a {{email}}. Haz clic en el enlace del email para completar tu registro.',
    backToLogin: 'Volver al inicio de sesión',
    optional: 'opcional',
    registerOrganizerTitle: 'Registro de Organizador',
    registerSingerTitle: 'Registro de Cantante',
    registerOrganizerDescription: 'Crea tu cuenta para organizar eventos de karaoke',
    registerSingerDescription: 'Crea tu cuenta para participar en eventos de karaoke',
    setPassword: 'Crear contraseña',
    setPasswordTitle: 'Finalizar inscripción',
    setPasswordDescription: 'Crea tu contraseña para finalizar tu inscripción',
    createPassword: 'Crear mi contraseña',
    passwordRequirements: 'Mínimo 12 caracteres, con mayúscula, minúscula, número y carácter especial',
    forgotPasswordDescription: 'Ingresa tu dirección de correo electrónico y te enviaremos un enlace para restablecer tu contraseña.',
    sendResetLink: 'Enviar enlace de restablecimiento',
    resetPasswordEmailSent: 'Email de restablecimiento enviado',
    resetPasswordInstructions: 'Se ha enviado un email de restablecimiento a {{email}}. Haz clic en el enlace del email para restablecer tu contraseña.',
    resetPassword: 'Restablecer contraseña',
    resetPasswordDescription: 'Crea una nueva contraseña para tu cuenta',
    resetPasswordButton: 'Restablecer mi contraseña',
    passwordResetSuccess: 'Contraseña restablecida',
    passwordResetSuccessDescription: 'Tu contraseña ha sido restablecida con éxito. Serás redirigido a la página de inicio de sesión.',
    resetLinkExpired: 'Enlace de restablecimiento expirado',
    resetLinkExpiredDescription: 'Este enlace de restablecimiento ha expirado (válido por 1 hora). Por favor, solicita un nuevo enlace.',
    requestNewResetLink: 'Solicitar un nuevo enlace',
    rememberPassword: '¿Recuerdas tu contraseña?',
  },
  // Organizer menu
  organizerMenu: {
    menu: 'Menú organizador',
    dashboard: 'Panel de control',
    venues: 'Lugares',
    settings: 'Configuración',
    dataPrivacy: 'Datos Personales',
    subscriptions: 'Precios',
    about: 'Acerca de',
    user: 'Usuario',
  },
  errors: {
    genericMessage: 'Ocurrió un error',
    required: 'Este campo es obligatorio',
    invalidEmail: 'Correo electrónico inválido',
    passwordTooShort: 'La contraseña debe tener al menos 12 caracteres',
    passwordRequirements: 'La contraseña debe contener al menos una mayúscula, una minúscula, un dígito y un carácter especial',
    passwordsDoNotMatch: 'Las contraseñas no coinciden',
    loginFailed: 'Error al iniciar sesión',
    registerFailed: 'Error al registrarse',
    resetPasswordFailed: 'Error al restablecer la contraseña',
    invalidToken: 'Token inválido o expirado',
    networkError: 'Error de red',
    unauthorized: 'No autorizado',
    forbidden: 'Acceso denegado',
    notFound: {
      title: 'Página no encontrada',
      description: 'La página que buscas no existe o ha sido movida.',
      explore: 'Explorar la aplicación',
    },
    server: {
      title: 'Error del servidor',
      description: 'Ocurrió un error inesperado. Por favor, inténtalo más tarde.',
    },
    generic: {
      title: 'Ups, algo salió mal',
      description: 'Ocurrió un error inesperado. Por favor, inténtalo de nuevo.',
    },
    serverError: 'Error del servidor',
    // Error codes from backend
    VALIDATION_ERROR: 'Error de validación',
    CREATE_EVENT_ERROR: 'Error al crear el evento',
    UPDATE_EVENT_ERROR: 'Error al actualizar el evento',
    EVENT_NOT_FOUND: 'Evento no encontrado',
    VENUE_NOT_FOUND: 'Lugar no encontrado',
    QUOTA_EXCEEDED_CREATE: 'Cuota mensual alcanzada para la creación de eventos',
    QUOTA_EXCEEDED_DATE_CHANGE: 'Cuota del plan {{planName}} alcanzada para este mes: Máximo {{limit}} evento(s) por mes. Tu cuota se reiniciará al final de tu período de facturación.',
    SUBSCRIPTION_EVENT_CANNOT_CONVERT: 'Los eventos incluidos en tu plan de suscripción no pueden convertirse en evento único',
    EVENT_DATE_NOT_REACHED: 'No se puede activar el evento antes de su fecha programada',
    EVENT_DATE_PAST: 'La fecha del evento no puede estar en el pasado',
    EVENT_DATE_AFTER_EXPIRATION: 'La fecha del evento no puede ser posterior a la fecha de expiración de su evento único',
    // Validation errors by field and Zod code
    validation: {
      name: {
        too_small: 'El nombre del evento es obligatorio',
        required: 'El nombre del evento es obligatorio',
        invalid_type: 'El nombre del evento es inválido',
      },
      date: {
        required: 'La fecha es obligatoria',
        invalid_type: 'La fecha es inválida',
        invalid_date: 'La fecha es inválida',
        invalid_string: 'El formato de fecha es inválido',
      },
      description: {
        invalid_type: 'La descripción es inválida',
      },
      venueId: {
        invalid_type: 'El lugar es inválido',
      },
    },
  },
  // Landing page
  landing: {
    languageLabel: 'Elige tu idioma',
    heroTitle: 'Tus veladas karaoke, simplificadas.',
    heroSubtitle: 'Crea tu evento en 2 minutos. Tus invitados escanean un código QR, añaden sus canciones — tú mantienes el control de la velada.',
    ctaPrimary: 'Crear mi primer karaoke — gratis',
    ctaOrganize: 'Quiero organizar',
    ctaOrganizeDesc: 'Crea eventos, gestiona la playlist, sigue los turnos.',
    ctaSing: 'Quiero cantar',
    ctaSingDesc: 'Únete a un evento con el código QR y añade tus canciones.',
    alreadyAccount: '¿Ya tienes cuenta?',
    login: 'Iniciar sesión',
    whyTitle: '¿Por qué KaraoNow?',
    benefit1Title: 'Un código QR, cero papel',
    benefit1Desc: 'Tus invitados escanean y eligen sus canciones desde el móvil. Se acabaron las listas perdidas.',
    benefit2Title: 'Playlist en directo',
    benefit2Desc: 'Orden de paso visible para todos. Avanzas con las canciones sin complicaciones.',
    benefit3Title: 'Gratis para empezar',
    benefit3Desc: 'Hasta 4 eventos gratis. Pasa a ilimitado cuando quieras.',
    twoWaysTitle: 'Dos formas de vivir KaraoNow',
    learnMore: 'Saber más',
  },
  home: {
    title: 'Gestión de listas de reproducción para eventos de Karaoke',
    wantToSing: 'Quiero cantar',
    wantToSingDescription: 'Únete a eventos y añade tus canciones',
    wantToOrganize: 'Quiero organizar',
    wantToOrganizeDescription: 'Crea y gestiona tus eventos de karaoke',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
  },
  dashboard: {
    title: 'Panel de Organizador',
    welcome: 'Bienvenido',
    events: 'Eventos',
    createEvent: 'Crear evento',
    noEvents: 'No hay eventos',
    noEventsDescription: 'Comienza creando tu primer evento de karaoke',
    filterAll: 'Todos',
    filterDraft: 'Borradores',
    filterActive: 'Activos',
    filterCompleted: 'Completados',
    filterCancelled: 'Cancelados',
    loadingEvents: 'Cargando eventos...',
    errorLoadingEvents: 'Error al cargar eventos',
    noEventsMatchingFilter: 'Ningún evento coincide con este filtro',
    tryDifferentFilter: 'Intenta seleccionar un filtro diferente',
  },
  events: {
    name: 'Evento',
    events: 'Eventos',
    song: 'Canción',
    songs: 'Canciones',
    description: 'Descripción',
    date: 'Fecha',
    time: 'Hora',
    venue: 'Lugar',
    selectVenue: 'Seleccionar un lugar',
    namePlaceholder: 'Mi noche de karaoke',
    descriptionPlaceholder: 'Descripción opcional...',
    createTitle: 'Crear Evento',
    createSubtitle: 'Crea un nuevo evento de karaoke',
    createButton: 'Crear Evento',
    createEvent: 'Crear Evento',
    createSuccess: 'Evento creado con éxito',
    createError: 'Error al crear el evento',
    noVenues: 'No hay lugares disponibles. Por favor, crea un lugar primero.',
    createVenue: 'Crear Lugar',
    status: 'Estado',
    statusDraft: 'Borrador',
    statusActive: 'Activo',
    statusCompleted: 'Completado',
    statusCancelled: 'Cancelado',
    statusHidden: 'Oculto',
    createdAt: 'Creado el',
    updatedAt: 'Actualizado el',
    view: 'Ver',
    open: 'Abrir',
    edit: 'Editar',
    delete: 'Eliminar',
    qrCode: 'Código QR',
    deleteConfirmTitle: 'Eliminar Evento',
    deleteConfirm: '¿Estás seguro de que quieres eliminar el evento "{{name}}" ?',
    deleteSuccess: 'Evento eliminado con éxito',
    deleteError: 'Error al eliminar el evento',
    managementTitle: 'Gestión del Evento',
    editEvent: 'Editar Evento',
    updateSuccess: 'Evento actualizado con éxito',
    updateError: 'Error al actualizar el evento',
    showQRCode: 'Mostrar Código QR',
    hideQRCode: 'Ocultar Código QR',
    qrCodeTitle: 'Código QR del Evento',
    qrCodeDescription: 'Comparte este código con tus participantes para que puedan unirse al evento',
    copyQRCode: 'Copiar Enlace',
    qrCodeCopied: 'Enlace copiado al portapapeles',
    shareQRCode: 'Compartir',
    queue: 'Cola',
    singers: 'Cantantes',
    noQueue: 'No hay canciones en la cola',
    noSingers: 'No hay cantantes registrados',
    guest: 'Invitado',
    singer: 'Cantante',
    songsCount_one: '{{count}} canción',
    songsCount_other: '{{count}} canciones',
    deleteQueueItem: 'Eliminar canción',
    deleteQueueItemConfirm: '¿Estás seguro de que deseas eliminar esta canción de la cola?',
    deleteSinger: 'Eliminar cantante',
    deleteSingerConfirm: '¿Estás seguro de que deseas eliminar este cantante? Todas sus canciones en la cola también serán eliminadas.',
    singerDeleteError: 'Error al eliminar el cantante',
    moveUp: 'Mover hacia arriba',
    moveDown: 'Mover hacia abajo',
    dragToReorder: 'Arrastrar para reorganizar',
    setCurrent: 'Marcar como actual',
    markCompleted: 'Marcar como completado',
    cancel: 'Cancelar',
    queueUpdateError: 'Error al actualizar la cola',
    queueDeleteError: 'Error al eliminar la canción',
    queueStatusPending: 'Pendiente',
    queueStatusCurrent: 'Actual',
    queueStatusCompleted: 'Completado',
    queueStatusCancelled: 'Cancelado',
    qrCodeUrl: 'URL del evento',
    qrCodeShareText: '¡Únete a este evento de karaoke: {{eventName}}!',
    qrCodePrintTitle: 'Código QR - KaraoNow',
    qrCodePrintInstructions: 'Escanea este código QR para acceder al evento y agregar tus canciones a la cola.',
    qrCodePrint: 'Imprimir',
    qrCodeShare: 'Compartir',
    qrCodeDownload: 'Descargar',
    startEvent: 'Iniciar el evento',
    start: 'Iniciar',
    completeEvent: 'Completar el evento',
    complete: 'Completar',
    cancelEvent: 'Cancelar el evento',
    cancelConfirmTitle: 'Confirmar cancelación',
    cancelConfirmMessage: '¿Está seguro de que desea cancelar este evento? Esta acción es definitiva.',
    cancelConfirmOneShotTitle: 'Confirmar cancelación - Evento único',
    cancelConfirmOneShotMessage: '⚠️ Atención: Este evento único fue comprado. La cancelación es definitiva y no se realizará ningún reembolso si el evento no ha sido utilizado. ¿Está seguro de que desea cancelar este evento?',
    cancelConfirmButton: 'Sí, cancelar',
    cancelCancelButton: 'No, mantener el evento',
    deleteEvent: 'Eliminar el evento',
    deleteEventConfirm: '¿Estás seguro de que quieres eliminar este evento? Esta acción es irreversible.',
    hide: 'Ocultar',
    hideConfirmTitle: 'Ocultar evento',
    hideConfirm: '¿Ocultar el evento "{{name}}" ? Ya no será visible en tu lista principal, pero seguirá contando en tu cuota mensual.',
    hideSuccess: 'Evento ocultado con éxito',
    hideError: 'Error al ocultar el evento',
    hidden: 'Oculto',
    showHidden: 'Ocultos',
    hiddenEventTitle: 'Evento oculto',
    hiddenEventMessage: 'Este evento ha sido ocultado y ya no es accesible. Sigue contando en tu cuota mensual.',
    hiddenEventTooltip: 'Este evento está oculto y no se puede ver',
    statusUpdated: 'Estado del evento actualizado',
    backToEvent: 'Volver al evento',
    // Quota exceeded for date change
    quotaExceededDateChange: 'Cuota mensual alcanzada para esta fecha',
    quotaExceededDateChangeTitle: 'Cuota mensual alcanzada',
    quotaExceededDateChangeExplanation: 'No puede cambiar la fecha del evento "{{eventName}}" ya que excedería su cuota mensual de eventos.',
    quotaExceededDateChangeOptions: 'Puede mantener la fecha original o convertir este evento en "Evento Único" para eliminarlo de su cuota mensual.',
    quotaExceededDetails: 'Tiene {{current}} evento(s) de {{limit}} para este período.',
    upgradeToOneShotOption: 'Opción: Pasar a Evento Único',
    upgradeToOneShotDescription: 'Al convertir este evento en "Evento Único", ya no contará en su cuota mensual y podrá cambiar su fecha libremente.',
    upgradeToOneShotNow: '🚀 Pasar a Evento Único',
    keepOriginalDate: 'Mantener la fecha original',
    multipleEventsPremiumSuggestion: '¿Varios eventos? El plan Premium podría ser más económico.',
    // Quota exceeded for event creation
    quotaExceededCreateTitle: 'Cuota mensual alcanzada',
    quotaExceededCreateMessage: 'Ha alcanzado el límite de su {{planName}}. No puede crear más eventos para este período.',
    quotaExceededCreateExplanation: 'Su cuota mensual de eventos está alcanzada. Puede esperar el final de su período de facturación o pasar a un plan superior.',
    quotaExceededCreateOptions: 'También puede crear un evento único que no contará en su cuota mensual.',
    // Quota exceeded for FREE plan (no monthly/billing period references)
    quotaExceededCreateTitleFree: 'Cuota alcanzada',
    quotaExceededCreateMessageFree: 'Ha alcanzado el límite de su {{planName}}. No puede crear más eventos.',
    quotaExceededCreateExplanationFree: 'Ha alcanzado el límite de eventos de su plan gratuito. Puede pasar a un plan superior para crear más eventos.',
    quotaExceededCreateOptionsFree: 'También puede crear un evento único que no contará en su cuota.',
    quotaExceededDetailsFree: 'Tiene {{current}} evento(s) de {{limit}}.',
    createOneShotDescriptionFree: 'Cree un evento único que no contará en su cuota.',
    upgradePlanDescriptionFree: 'Pase a un plan superior para aumentar su cuota de eventos.',
    createOneShotOption: 'Opción: Crear un Evento Único',
    createOneShotDescription: 'Cree un evento único que no contará en su cuota mensual.',
    createOneShotNow: '🚀 Crear un Evento Único',
    upgradePlanOption: 'Opción: Pasar al plan superior',
    upgradePlanDescription: 'Pase a un plan superior para aumentar su cuota mensual de eventos.',
    upgradePlanNow: 'Pasar al plan superior',
    quotaResetInfo: 'Su cuota se restablecerá al final de su período de facturación.',
    // Generic quota exceeded message
    quotaExceeded: 'Cuota mensual alcanzada para esta fecha',
    // Plan subscription badge
    planSubscription: 'Incluido en el plan',
    planSubscriptionTooltip: 'Evento incluido en tu suscripción',
  },
  // Venues
  venues: {
    title: 'Gestión de Lugares',
    subtitle: 'Crea y gestiona tus lugares de karaoke',
    venuesList: 'Lista de Lugares',
    addVenue: 'Agregar Lugar',
    editVenue: 'Editar Lugar',
    noVenues: 'No hay lugares registrados',
    noVenuesDescription: 'Comienza creando tu primer lugar',
    eventName: 'Nombre del evento',
    eventNamePlaceholder: 'Karaoke de {{firstName}}',
    name: 'Nombre del Lugar',
    namePlaceholder: 'Ej: Bar Le Chat Noir',
    address: 'Dirección',
    addressPlaceholder: 'Ej: Calle de la Música 123',
    city: 'Ciudad',
    cityPlaceholder: 'Ej: Madrid',
    country: 'País',
    countryPlaceholder: 'Ej: España',
    googleMapsUrl: 'Enlace de Google Maps',
    googleMapsUrlPlaceholder: 'https://maps.google.com/...',
    viewOnGoogleMaps: 'Ver en Google Maps',
    addSuccess: 'Lugar creado con éxito',
    addError: 'Error al crear el lugar',
    updateSuccess: 'Lugar actualizado con éxito',
    updateError: 'Error al actualizar el lugar',
    deleteSuccess: 'Lugar eliminado con éxito',
    deleteError: 'Error al eliminar el lugar',
    confirmDelete: '¿Estás seguro de que quieres eliminar el lugar "{{name}}" ?',
    confirmDeleteTitle: 'Eliminar Lugar',
    cancel: 'Cancelar',
    add: 'Agregar',
    edit: 'Editar',
    save: 'Guardar',
    deleting: 'Eliminando...',
    saving: 'Guardando...',
    generateMapsUrl: 'Generar enlace de Google Maps',
    generateMapsUrlError: 'Por favor, complete al menos el nombre del lugar o la ciudad para generar el enlace',
    mapsModal: {
      title: 'Seleccionar ubicación en Google Maps',
      description: 'Usa Google Maps para encontrar y ajustar con precisión la ubicación de tu lugar.',
      instructions: 'Haz clic en "Abrir en Google Maps" a continuación, ajusta el punto en el mapa si es necesario, luego copia el enlace de la barra de direcciones de tu navegador y pégalo en el campo a continuación.',
      urlLabel: 'Enlace de Google Maps',
      openInMaps: 'Abrir en Google Maps',
      confirm: 'Confirmar',
      clickToOpen: 'Haz clic para abrir Google Maps',
      clickDescription: 'Se abrirá una nueva ventana con Google Maps para que puedas ajustar la ubicación',
    },
  },
  // Singer interface
  singer: {
    // Dashboard
    dashboard: 'Panel de control',
    dashboardSubtitle: 'Ve tus eventos y canciones',
    myEvents: 'Mis eventos',
    mySongs: 'Mis canciones',
    allEvents: 'Todos',
    activeEvents: 'Activos',
    pastEvents: 'Pasados',
    inactiveEvents: 'Inactivos',
    sortByDate: 'Ordenar por fecha',
    sortByOrganizer: 'Ordenar por organizador',
    sortBySongs: 'Ordenar por número de canciones',
    noEvents: 'No has participado en ningún evento todavía.',
    noSongs: 'No has añadido ninguna canción todavía.',
    eventName: 'Nombre del evento',
    organizer: 'Organizador',
    date: 'Fecha',
    songsAdded: 'Canciones añadidas',
    songsSung: 'Canciones cantadas',
    status: 'Estado',
    status_active: 'Activo',
    status_completed: 'Completado',
    status_cancelled: 'Cancelado',
    status_draft: 'Borrador',
    title: 'Título',
    artist: 'Artista',
    event: 'Evento',
    addedDate: 'Fecha de añadido',
    totalEvents: 'Eventos totales',
    totalSongs: 'Canciones totales',
    uniqueOrganizers: 'Organizadores únicos',
    // Settings
    settings: 'Configuración',
    settingsSubtitle: 'Gestiona tu perfil y tus datos',
    menu: 'Menú del cantante',
    profile: 'Perfil',
    changePassword: 'Cambiar contraseña',
    changePasswordDescription: 'Te enviaremos un enlace para restablecer tu contraseña por correo electrónico.',
    sendResetLink: 'Enviar enlace de restablecimiento',
    passwordResetLinkSent: 'Se ha enviado un enlace de restablecimiento de contraseña a tu correo electrónico.',
    accountData: 'Datos de la cuenta',
    editProfile: 'Editar mi perfil',
    saveChanges: 'Guardar cambios',
    profileUpdated: 'Perfil actualizado correctamente',
    changePasswordTitle: 'Cambiar mi contraseña',
    currentPassword: 'Contraseña actual',
    newPassword: 'Nueva contraseña',
    confirmNewPassword: 'Confirmar nueva contraseña',
    passwordRequirements: 'La contraseña debe contener al menos 12 caracteres, una mayúscula, una minúscula, un dígito y un carácter especial.',
    passwordChanged: 'Contraseña cambiada correctamente',
    passwordTooShort: 'La contraseña debe contener al menos 12 caracteres',
    passwordsNotMatch: 'Las contraseñas no coinciden',
    currentPasswordRequired: 'Se requiere la contraseña actual',
    // Errores de validación de contraseña
    passwordMinLength: 'La contraseña debe contener al menos 12 caracteres',
    passwordNoUppercase: 'La contraseña debe contener al menos una mayúscula',
    passwordNoLowercase: 'La contraseña debe contener al menos una minúscula',
    passwordNoDigit: 'La contraseña debe contener al menos un dígito',
    passwordNoSpecialChar: 'La contraseña debe contener al menos un carácter especial',
    exportData: 'Exportar mis datos',
    exportDataDescription: 'Descarga una copia de todos tus datos personales en formato JSON.',
    downloadData: 'Descargar mis datos',
    dataExported: 'Datos exportados correctamente',
    dangerZone: 'Zona de peligro',
    deleteAccountDescription: 'Eliminar tu cuenta es irreversible. Todos tus datos se eliminarán permanentemente.',
    deleteAccount: 'Eliminar mi cuenta',
    deleteAccountConfirm: 'Eliminar',
    confirmDeleteAccount: '¿Estás seguro de que deseas eliminar tu cuenta? Esta acción es irreversible.',
    // QR Code Landing
    qrCodeMissing: 'Token QR code faltante',
    qrCodeError: 'Error al validar el código QR',
    eventNotFound: 'Evento no encontrado o token inválido',
    organizerNotAllowed: 'Eres organizador. Usa tu panel de control para gestionar este evento.',
    accessDenied: 'Acceso no autorizado',
    validatingQRCode: 'Validando código QR...',
    chooseAccessMethod: 'Elige cómo unirte al evento',
    continueAsGuest: 'Continuar como invitado',
    orWithAccount: 'o con una cuenta',
    enterPseudo: 'Ingresa tu seudónimo para unirte al evento',
    joinEvent: 'Unirse al evento',
    joining: 'Conectando...',
    sessionError: 'Error al crear la sesión',
    joinError: 'Error al unirse al evento',
    sessionExpired: 'Tu sesión ha expirado. Por favor, escanea el código QR del evento nuevamente.',
    guestMode: 'Modo invitado',
    leaveEvent: 'Salir del evento',
    // Singer Event Page
    eventNotActive: '¡Este evento aún no está activo. Vuelve más tarde!',
    eventNotStarted: 'Este evento aún no ha comenzado. ¡Estará disponible una vez que el organizador inicie el karaoke!',
    eventNotYetAvailable: 'Este evento aún no está disponible. Por favor, espere hasta la fecha programada.',
    eventEnded: 'Este evento ha terminado. Los votos ya no se aceptan.',
    eventCancelled: 'Este evento ha sido cancelado. Los votos no están disponibles.',
    searchSong: 'Buscar una canción',
    searchResults: 'Resultados de búsqueda',
    advancedSearch: 'Búsqueda avanzada',
    advancedSearchToggle: 'Búsqueda avanzada',
    simpleSearch: 'Búsqueda simple',
    searchPlaceholder: 'Título o artista...',
    titlePlaceholder: 'Título...',
    artistPlaceholder: 'Artista...',
    noResults: 'No se encontraron resultados',
    addCustomSong: 'Añadir este título',
    addCustomSongDescription: 'Este título se guardará en la base de datos y se añadirá a tu cola.',
    addAndQueue: 'Añadir y poner en cola',
    localResults: '{{count}} resultado(s) encontrado(s) en nuestra base',
    musicbrainzResults: '{{count}} resultado(s) de MusicBrainz',
    moreResults: 'Más resultados',
    addError: 'Error al agregar a la cola',
    queueLimitReached: 'Límite alcanzado: el organizador de este evento está usando el plan gratuito que está limitado a {{limit}} canción(es) máximo en la cola. La cola contiene actualmente {{currentCount}} canción(es). Por favor, contacte al organizador si desea agregar más canciones.',
    songAdded: '"{{title}}" añadido a la cola',
    myQueue: 'Mis canciones',
    noSongsInQueue: 'Aún no has añadido canciones',
    fullQueue: 'Cola',
    queueEmpty: 'La cola está vacía',
    you: 'Tú',
    unknown: 'Desconocido',
    // Queue statuses
    statusPending: 'Pendiente',
    statusCurrent: 'En curso',
    statusCompleted: 'Completado',
    statusCancelled: 'Cancelado',
  },
  // Singer menu
  singerMenu: {
    about: 'Acerca de',
    dataPrivacy: 'Datos Personales',
  },
  // Subscription and Plans
  subscription: {
    title: 'Planes y precios',
    subtitle: 'Elige el plan que se adapte a tus necesidades',
    currentPlan: 'Plan actual',
    planFree: 'Plan Gratuito',
    planOneShot: 'Evento Único',
    planPro: 'Plan Pro',
    planPremium: 'Plan Premium',
    planUltimate: 'Plan Ultimate',
    planFreeDescription: 'Perfecto para empezar',
    planOneShotDescription: 'Para un evento',
    planProDescription: 'Para organizadores regulares',
    planPremiumDescription: 'Para entusiastas',
    planUltimateDescription: 'Para profesionales',
    priceFreeFree: 'Gratis',
    priceOneShotPer: '{{price}}€ / evento',
    priceProPer: '{{price}}€ / mes',
    pricePremiumPer: '{{price}}€ / mes',
    priceUltimatePer: '{{price}}€ / mes',
    maxEvents: 'Eventos máximos',
    maxSongsPerEvent: 'Canciones por evento',
    unlimited: 'Ilimitado',
    features: 'Características',
    currentPlanBadge: 'Plan actual',
    changePlan: 'Cambiar plan',
    upgradePlan: 'Actualizar a premium',
    upgradePlanButton: 'Pasar al plan superior',
    selectPlan: 'Seleccionar este plan',
    monthlyQuota: 'Cuota mensual',
    freeQuota: 'Cuota gratuita',
    eventsUsed: '{{current}} / {{limit}} eventos',
    billingPeriod: 'Período: {{start}} - {{end}}',
    quotaReached: 'Cuota mensual alcanzada',
    quotaReachedMessage: 'Ha alcanzado su límite de eventos para este período. La cuota se restablecerá al final de su período de facturación.',
    quotaWarning: 'Cuota mensual casi alcanzada',
    quotaWarningMessage: 'Le quedan {{remaining}} evento(s) para este período.',
    quotaInfo: 'Le quedan {{remaining}} evento(s) para este período.',
    learnMore: 'Más información',
    contactSupport: 'Contactar soporte',
    freePlanNote: 'El plan gratuito está limitado a {{maxEvents}} eventos (todos los estados, excluyendo Evento Único) y {{maxSongsPerEvent}} canciones por evento.',
    oneShotPlanNote: 'Número ilimitado de canciones en la lista de reproducción del evento. Válido por 15 días.',
    proPlanNote: '{{maxEvents}} eventos por mes (período móvil). Canciones ilimitadas.',
    premiumPlanNote: '{{maxEvents}} eventos por mes (período móvil). Canciones ilimitadas.',
    ultimatePlanNote: 'Eventos y canciones ilimitados.',
    noEngagement: 'Sin compromiso.',
    convertEvent: 'Convertir un evento',
    createEvent: 'Crear un evento',
    chooseOption: 'Elige una opción:',
    convertDescription: 'Selecciona un evento gratuito y conviértelo a un plan de Evento Único.',
    createDescription: 'Crea un nuevo evento con el plan de Evento Único.',
    noFreeEventsToConvert: 'No tienes un evento GRATUITO disponible para convertir.',
    selectFreeEvent: 'Selecciona un evento GRATUITO para convertir:',
    convertThisEvent: 'Convertir este evento',
    createNewOneShot: 'Crear un evento de Evento Único',
    newOneShotEventName: 'Nuevo evento único',
    createEventError: 'Error al crear el evento',
    createOneShotEventTitle: 'Crear un evento único',
    proceedToPayment: 'Proceder al pago',
    // Stripe checkout
    checkoutError: 'Ocurrió un error durante el pago',
    paymentSuccessful: '¡Pago exitoso!',
    paymentProcessing: 'Tu pago se está procesando. Serás redirigido en breve.',
    redirectingIn5: 'Redirección automática en 5 segundos...',
    backToSubscription: 'Volver a planes',
    successTitle: 'Pago confirmado',
    successSubtitle: 'Gracias por tu compra',
    paymentCancelled: 'Pago cancelado',
    checkoutCancelledMessage: 'Cancelaste el pago. Puedes intentar de nuevo en cualquier momento.',
    cancelledTitle: 'Pago cancelado',
    cancelledSubtitle: 'Vuelve a los planes para intentar de nuevo',
    whatHappensNext: '¿Qué sucede a continuación?',
    webhooksProcessing: 'Estamos procesando tu pago',
    subscriptionActive: 'Tu suscripción estará activa pronto',
    confirmationEmail: 'Recibirás un correo de confirmación',
    // Subscription cancellation
    cancelConfirm: '¿Estás seguro de que deseas cancelar tu suscripción?',
    cancelDetails: 'Mantendrás acceso a tu plan Premium hasta el final de tu período de facturación actual. Después de esa fecha, serás degradado al plan Gratuito.',
    cancelSuccess: 'Tu suscripción ha sido cancelada correctamente.',
    cancelPlan: 'Cancelar',
    cancellationInProgress: 'La cancelación de tu suscripción ya está en curso. Serás cambiado al plan Gratuito al final de tu período de facturación.',
    cancellationInProgressTitle: 'Cancelación en Curso',
    cancellationInProgressMessage: 'La cancelación de tu suscripción ya está en curso. Serás cambiado al plan Gratuito al final de tu período de facturación actual. No se requiere ninguna acción adicional.',
    accessUntil: 'Tendrás acceso hasta el {{date}}.',
    cancelError: 'Ocurrió un error al cancelar tu suscripción',
    cancelModalTitle: 'Cancelar Suscripción',
    cancelModalMessage: 'Conservarás el acceso a tu plan Premium hasta el final de tu período de facturación actual. Después de esa fecha, serás cambiado automáticamente al plan Gratuito.',
    cancelModalConfirm: 'Sí, cancelar mi suscripción',
    endOfBillingPeriod: 'fin del período de facturación',
    // ONE_SHOT upgrade
    upgradeToOneShot: 'Actualizar a Evento Único',
    upgradeOneShotConfirm: 'Estás a punto de actualizar el evento "{{eventName}}" al plan Evento Único.',
    oneShotDescription: 'Este es un pago único para canciones ilimitadas en este evento específico.',
    price: 'Precio',
    oneTimePayment: 'Pago único',
    whatYouGet: 'Lo que obtendrás:',
    unlimitedSongs: 'Canciones ilimitadas para este evento',
    noLimits: 'Sin límites de cola',
    oneShotValidity: 'Válido hasta la finalización del evento',
    upgradeNow: 'Actualizar ahora',
    upgradeError: 'Error al actualizar el evento',
    updateSuccess: 'Suscripción actualizada con éxito al plan {{plan}}',
    changePlanTitle: 'Cambiar de plan',
    changePlanSubtitle: 'Está a punto de modificar su suscripción',
    newPlan: 'Nuevo plan',
    perMonth: 'mes',
    upgradeCost: 'Costo adicional: +{{amount}}€ / mes',
    downgradeCredit: 'Crédito estimado: -{{amount}}€ / mes',
    immediateChange: 'Cambio inmediato',
    immediateChangeDescription: 'El cambio de plan será efectivo inmediatamente. La facturación prorrateada se calculará automáticamente por Stripe.',
    confirmChange: 'Confirmar cambio',
    oneShot: 'Evento único',
    oneShotTooltip: 'Suscripción evento único',
    expiresLabel: 'Expira el',
    oneShotExpirationTitle: 'Expiración de su evento único',
    oneShotDaysRemaining: '{{count}} día restante',
    oneShotDaysRemaining_plural: '{{count}} días restantes',
    oneShotExpiresOn: 'Expira el {{date}}',
    oneShotExpirationWarning: '⚠️ Su evento volverá al plan gratuito después de esta fecha. ¡Asegúrese de usarlo antes de que expire!',
    oneShotValidityInfo: 'Esta compra es válida durante {{days}} días desde la fecha de compra. ¡Úsela antes de que expire!',
    upgradeToUnlimited: '🚀 Pasar a ilimitado',
    upgradeToUnlimitedNow: '🚀 Pasar a ilimitado ahora',
    upgradeSingleEventConfirm: 'Está a punto de pasar el evento "{{eventName}}" a ilimitado.',
    singleEventDescription: 'Se trata de un pago único para canciones ilimitadas en este evento específico.',
    singleEventPlan: 'Evento único',
    multiplePremiumSuggestion: '¿Varios eventos? El plan Premium podría ser más rentable.',
    comparePlans: 'Comparar planes →',
    premium: 'PREMIUM',
  },
  // Plan alerts
  planAlert: {
    limitReached: 'Límite del plan alcanzado',
    songsLimitReached: 'Has alcanzado el límite del plan gratuito ({{limit}} canción(es)). Para agregar más canciones, suscríbete a un plan pagado.',
    eventsLimitReached: 'Has alcanzado el límite del plan gratuito ({{limit}} evento(s)). Para crear más eventos, suscríbete a un plan pagado.',
    upgrade: 'Suscribirse',
    dismissible: true,
  },
  pwa: {
    install: 'Instalar',
    installApp: 'Instalar aplicación',
    installPrompt: '¡Instala KaraoNow para un acceso rápido!',
    installed: 'Aplicación instalada',
    offline: 'Estás sin conexión',
    offlineMessage: 'Algunas funciones pueden estar limitadas.',
    updateAvailable: 'Actualización disponible',
    updateNow: 'Actualizar ahora',
  },
  // About
  about: {
    title: 'Acerca de KaraoNow',
    subtitle: 'Descubre la app que transforma tus eventos de karaoke',
    introduction: '¿Qué es KaraoNow?',
    introductionText: 'KaraoNow es una plataforma SaaS innovadora diseñada para gestionar tus eventos de karaoke de manera fluida y profesional. Ya seas un organizador de eventos o un cantante apasionado, KaraoNow te proporciona las herramientas necesarias para crear una experiencia de karaoke inolvidable.',
    missionText: 'Nuestra misión es simplificar la gestión de listas de reproducción de karaoke y crear una conexión auténtica entre organizadores y cantantes, haciendo que cada evento sea memorable y bien organizado.',
    features: 'Características principales',
    feature1Title: 'Gestión de eventos',
    feature1Desc: 'Crea y gestiona fácilmente tus eventos de karaoke con todos los detalles necesarios.',
    feature2Title: 'Participación interactiva',
    feature2Desc: 'Permite que los cantantes se unan a tus eventos mediante código QR y voten por sus canciones favoritas.',
    feature3Title: 'Lista de reproducción en tiempo real',
    feature3Desc: 'Ve y gestiona la cola de canciones en directo con actualizaciones instantáneas.',
    feature4Title: 'Estadísticas detalladas',
    feature4Desc: 'Analiza el rendimiento de tus eventos con gráficos y métricas en tiempo real.',
    feature5Title: 'Seguridad mejorada',
    feature5Desc: 'Tus datos están protegidos por altos estándares de seguridad e infraestructura en la nube robusta.',
    feature6Title: 'Soporte multilingüe',
    feature6Desc: 'KaraoNow soporta 8 idiomas para llegar a una audiencia global.',
    technology: 'Stack tecnológico',
    frontend: 'Frontend',
    backend: 'Backend',
    version: 'Versión actual',
    versionNote: 'Actualizada regularmente con nuevas funciones',
    visitWebsite: 'Sitio web oficial',
    support: 'Soporte',
    supportNote: 'No dudes en contactarnos con cualquier pregunta',
    copyright: 'Todos los derechos reservados.',
    privacyNotice: 'Consulta nuestras páginas legales para obtener más información sobre la protección de datos.',
    tryForFree: 'Comienza tu prueba gratuita',
  },
  // Organizer
  organizer: {
    // Dashboard
    dashboard: 'Panel de control',
    dashboardSubtitle: 'Administra tus eventos de karaoke',
    menu: 'Menú del organizador',
    // Settings
    settings: 'Configuración',
    settingsSubtitle: 'Gestiona tu perfil y configuración',
    editProfile: 'Editar mi perfil',
    saveChanges: 'Guardar cambios',
    profileUpdated: 'Perfil actualizado correctamente',
    emailChangeConfirmTitle: 'Modificación de la dirección de correo electrónico',
    emailChangeConfirmMessage: 'Se enviará un correo de confirmación a su nueva dirección de correo electrónico. Su nuevo correo electrónico solo será efectivo después de validar el enlace en ese correo. Su correo electrónico anterior permanece activo hasta la validación.',
    changePassword: 'Cambiar contraseña',
    changePasswordDescription: 'Te enviaremos un enlace para restablecer tu contraseña por correo electrónico.',
    sendResetLink: 'Enviar enlace de restablecimiento',
    passwordResetLinkSent: 'Se ha enviado un enlace de restablecimiento de contraseña a tu correo electrónico.',
    accountData: 'Datos de la cuenta',
    exportData: 'Exportar mis datos',
    exportDataDescription: 'Descarga una copia de todos tus datos personales en formato JSON.',
    downloadData: 'Descargar mis datos',
    dataExported: 'Datos exportados correctamente',
    dangerZone: 'Zona de peligro',
    deleteAccountDescription: 'Eliminar tu cuenta es irreversible. Todos tus datos serán eliminados permanentemente.',
    deleteAccount: 'Eliminar mi cuenta',
    deleteAccountConfirm: 'Eliminar',
    confirmDeleteAccount: '¿Estás seguro de que deseas eliminar tu cuenta? Esta acción es irreversible.',
    // Plan section
    planSection: 'Mi Suscripción',
    planSectionDescription: 'Ver tu plan actual, límites y opciones de mejora',
    renewDate: 'Renovación el ',
    viewPlanDetails: 'Ver detalles del plan',
    eventsLimit: 'Eventos creados (excluyendo Evento Único)',
    subscriptionStatus: 'Estado',
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    statusScheduledEnd: 'Fin Programado',
    subscriptionExpires: 'Caduca el',
    subscriptionRenewsOn: 'Se renueva el',
    cancellationConfirmed: '¡Cancelación Confirmada!',
    continueAccessUntil: 'Continúa disfrutando de tu plan Premium hasta el {{date}}',
    autoDowngradeToFree: 'Cambiarás automáticamente al plan Gratuito después de esta fecha.',
    accessUntilEndOfPeriod: 'Conservas el acceso hasta el final de tu período de facturación actual.',
    planFeatures: 'Características del plan',
    featureEvents: 'Eventos por mes',
    featureSongsPerEvent: 'Canciones por evento',
    unlimited: 'Ilimitado',
    upgradePlan: 'Mejorar mi plan',
    managePlan: 'Gestionar mi suscripción',
  },
  // Email change
  email: {
    changeEmail: 'Cambiar mi dirección de correo',
    changeEmailDescription: 'Para cambiar tu dirección de correo, primero debes confirmar la nueva dirección. Se enviará un correo de confirmación a la nueva dirección.',
    currentEmail: 'Correo actual',
    newEmail: 'Nueva dirección de correo',
    newEmailAddress: 'Nueva dirección',
    requestEmailChange: 'Solicitar cambio de correo',
    emailRequired: 'La dirección de correo es requerida',
    emailUnchanged: 'La nueva dirección de correo debe ser diferente de la actual',
    emailChangeConfirmationSent: 'Se ha enviado un correo de confirmación a tu nueva dirección de correo.',
    emailChangeInfo: 'Tu correo anterior permanece activo hasta que la nueva dirección sea validada.',
    verifying: 'Verificando',
    verifyingDescription: 'Verificando tu dirección de correo...',
    success: 'Correo confirmado',
    confirmationSuccess: '¡Tu dirección de correo ha sido cambiada exitosamente!',
    confirmationFailed: 'Falló la confirmación',
    linkExpired: 'Enlace expirado',
    tokenExpired: 'El enlace de confirmación ha expirado. Por favor solicita uno nuevo.',
    linkExpiredDescription: 'El enlace de confirmación ha expirado (válido por 24 horas). Por favor solicita un nuevo enlace de confirmación.',
    redirecting: 'Redirigiendo...',
    redirectingToLogin: 'Redirigiendo a la conexión en 3 segundos...',
    goToSettings: 'Ir a configuración',
    backToSettings: 'Volver a configuración',
    requestNewLink: 'Solicitar un nuevo enlace',
    missingToken: 'Token de confirmación ausente.',
    validationPending: 'Validación pendiente',
    pendingEmailAddress: 'En espera de validación',
  },
  // RGPD - Privacidad de Datos
  rgpd: {
    pageTitle: 'Datos Personales',
    pageSubtitle: 'Gestiona tus datos y cuenta de forma segura',
    exportData: 'Descargar Mis Datos',
    exportDataDescription: 'Descarga una copia completa de tus datos personales en formato JSON. Puedes usar estos datos para importarlos en otro lugar o simplemente conservarlos como copia de seguridad.',
    exportIncludes: 'Esta exportación incluye',
    exportProfile: 'Perfil y preferencias',
    exportHistory: 'Historial de eventos',
    exportEvents: 'Eventos creados',
    exportSubscription: 'Información de suscripción',
    downloadData: 'Descargar (JSON)',
    exporting: 'Descargando...',
    exportFormatJSON: 'Formato: JSON (compatible con RGPD)',
    dataExportedSuccess: '✅ ¡Tus datos han sido descargados exitosamente!',
    deleteAccount: 'Eliminar Mi Cuenta',
    deleteAccountDescription: 'Esta acción eliminará permanentemente tu cuenta y todos los datos asociados. Esta operación no se puede deshacer.',
    deleteWarning: '⚠️ Advertencia Importante',
    deleteIrreversible: 'Esta acción es permanente e irreversible',
    deleteAllData: 'Todos tus datos serán eliminados',
    deleteNoRecovery: 'No habrá posibilidad de recuperación',
    deleteRecommendation: 'Recomendamos que descargues tus datos antes de eliminar tu cuenta.',
    deleteAccountButton: 'Eliminar Mi Cuenta',
    confirmDeletion: 'Confirmar eliminación',
    deleteConfirmMessage: '¿Estás seguro de que deseas eliminar tu cuenta? Esta acción no se puede deshacer.',
    continueDelete: 'Continuar a la eliminación',
    finalDeleteWarning: 'Confirmación Final - Sin Retorno',
    finalDeleteMessage: '¡Esta es tu última oportunidad! Hacer clic en "Eliminar Permanentemente" eliminará tu cuenta de forma irreversible.',
    permanentlyDelete: 'Eliminar Permanentemente',
    gdprCompliance: 'Cumplimiento de RGPD',
    gdprInfo: 'Según las regulaciones de RGPD, tienes derecho a acceder, rectificar, eliminar y portar tus datos personales. Estas herramientas te permiten ejercer estos derechos.',
  },
  // Cookies consent
  cookies: {
    title: '🍪 Usamos cookies',
    description: 'Usamos cookies para mejorar tu experiencia. Consulta nuestra política para más información.',
    understood: 'Entendido',
    learnMore: 'Más información',
    footerNote: 'Puedes consultar nuestra Política de cookies para más detalles.',
  },
  // Cookies policy page
  cookiesPage: {
    title: 'Política de Cookies',
    subtitle: 'Información completa sobre las cookies utilizadas en KaraoNow',
    overview: 'Descripción General',
    overviewText: 'KaraoNow utiliza cookies para mejorar tu experiencia de usuario, asegurar tu cuenta y comprender cómo utilizas nuestra plataforma. Esta política explica cómo y por qué utilizamos cookies.',
    whatAreCookies: '¿Qué son las Cookies?',
    whatAreCookiesText: 'Las cookies son pequeños archivos de texto almacenados en tu dispositivo. Permiten que los sitios web recuerden información sobre tus visitas, como tus preferencias y datos de inicio de sesión.',
    typesOfCookies: 'Tipos de Cookies',
    sessionCookies: 'Cookies de Sesión',
    sessionCookiesText: 'Estas cookies expiran cuando cierras tu navegador. Se utilizan para mantener tu sesión activa y asegurar tu acceso.',
    persistentCookies: 'Cookies Persistentes',
    persistentCookiesText: 'Estas cookies permanecen almacenadas en tu dispositivo durante un período definido. Nos permiten reconocerte en tus próximas visitas.',
    howWeUseCookies: 'Cómo Utilizamos las Cookies',
    useAuthentication: 'Autenticación y seguridad de sesión',
    usePreferences: 'Recordar tus preferencias (idioma, tema)',
    useSecurity: 'Protección contra abusos y ataques',
    usePerformance: 'Mejora del rendimiento de la plataforma',
    yourRights: 'Tus Derechos',
    yourRightsText: 'Tienes derecho a controlar las cookies. Puedes aceptar o rechazar cookies no esenciales a través de nuestro banner de consentimiento.',
    managingCookies: 'Gestionar Cookies',
    managingCookiesText: 'Puedes gestionar las cookies mediante la configuración de tu navegador. Ten en cuenta que desactivar ciertas cookies puede afectar el funcionamiento de KaraoNow.',
    contact: 'Contacto',
    contactText: 'Si tienes alguna pregunta sobre esta política, contáctanos a través de nuestra página de contacto o términos de servicio.',
    footer: '¿Tienes preguntas? Vuelve a casa.',
  },
  // Footer
  footer: {
    legal: 'Aviso legal',
    privacy: 'Política de privacidad',
    cookies: 'Política de cookies',
    terms: 'Términos y Condiciones',
  },
  // Página de aviso legal
  legalPage: {
    title: 'Aviso Legal',
    subtitle: 'Información legal sobre KaraoNow',
    editor: {
      title: 'Editor del sitio',
      name: 'axialdata',
      address: 'Impasse du pélican, 97233 Schoelcher, Martinica',
      phone: 'Teléfono: +596696367741',
      email: 'Correo: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: 'Director de publicación',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'Alojamiento',
    },
    dataProtection: {
      title: 'Protección de datos',
      description: 'Para obtener más información sobre la protección de sus datos personales, consulte nuestra política de privacidad.',
      link: 'Leer nuestra política de privacidad',
    },
  },
  // Página de política de privacidad
  privacyPage: {
    title: 'Política de Privacidad',
    subtitle: 'Cómo protegemos sus datos personales',
    lastUpdated: 'Última actualización: 1 de enero de 2025',
    intro: {
      title: 'Introducción',
      description: 'KaraoNow se compromete a proteger su privacidad. Esta política explica cómo recopilamos, utilizamos y protegemos sus datos personales en cumplimiento con el RGPD.',
    },
    dataCollection: {
      title: 'Datos recopilados',
      organizers: {
        title: 'Para organizadores',
        email: 'Dirección de correo electrónico',
        name: 'Nombre, apellido y organización',
        organization: 'Detalles de la organización',
        payment: 'Información de pago (a través de procesador de terceros)',
        events: 'Información sobre eventos creados',
      },
      singers: {
        title: 'Para cantantes',
        email: 'Dirección de correo electrónico (opcional)',
        pseudo: 'Seudónimo de usuario',
        name: 'Nombre y apellido (opcional)',
        participation: 'Eventos en los que ha participado',
        history: 'Historial de canciones añadidas a la cola',
      },
    },
    purposes: {
      title: 'Uso de sus datos',
      authentication: 'Autenticación y gestión de cuenta',
      eventManagement: 'Creación y gestión de eventos',
      songManagement: 'Gestión de colas de canciones',
      communication: 'Comunicaciones importantes sobre su cuenta',
      billing: 'Procesamiento de pagos y facturación',
      support: 'Atención al cliente y asistencia técnica',
      preferences: 'Mejora de su experiencia de usuario',
    },
    legalBasis: {
      title: 'Base legal del tratamiento',
      description: 'El tratamiento de sus datos se basa en su consentimiento al registrarse y en la ejecución del contrato de servicio entre usted y KaraoNow.',
    },
    retention: {
      title: 'Período de retención de datos',
      active: 'Datos activos: retenidos mientras su cuenta esté activa',
      inactive: 'Datos inactivos: eliminados 12 meses después del último inicio de sesión',
      legal: 'Datos legales: retenidos mientras sea requerido por ley',
    },
    rights: {
      title: 'Sus derechos RGPD',
      description: 'Bajo el RGPD, tiene los siguientes derechos:',
      access: 'Derecho a acceder a sus datos personales',
      rectification: 'Derecho a rectificar sus datos',
      deletion: 'Derecho al olvido (eliminación de datos)',
      portability: 'Derecho a la portabilidad de datos',
      opposition: 'Derecho a oponerse al tratamiento',
      limitation: 'Derecho a limitar el tratamiento',
      contact: 'Para ejercer estos derechos, contáctenos en support@karaonow.com',
    },
    security: {
      title: 'Seguridad',
      description: 'Utilizamos medidas de seguridad apropiadas para proteger sus datos contra acceso no autorizado, modificación o divulgación.',
    },
    cookies: {
      title: 'Cookies',
      description: 'KaraoNow utiliza cookies para mejorar su experiencia de usuario. Para obtener más información, consulte nuestra política de cookies.',
      link: 'Leer nuestra política de cookies',
    },
    contact: {
      title: 'Contacto',
      description: 'Para cualquier pregunta sobre esta política o sus datos personales, contáctenos:',
      email: 'support@karaonow.com',
    },
  },
  // Página de términos de servicio
  termsPage: {
    title: 'Términos y Condiciones',
    subtitle: 'Términos y condiciones de KaraoNow',
    lastUpdated: 'Última actualización: 1 de enero de 2025',
    intro: {
      title: 'Introducción',
      description: '¡Bienvenido a KaraoNow! Al usar nuestra plataforma, acepta estos términos y condiciones. Por favor, léalos cuidadosamente.',
    },
    service: {
      title: 'Descripción del servicio',
      description: 'KaraoNow es una plataforma SaaS para gestionar eventos de karaoke. La plataforma ofrece dos interfaces principales:',
      organizers: {
        title: 'Para organizadores',
        description: 'Los organizadores pueden crear eventos, generar códigos QR para compartir con cantantes, gestionar colas de canciones y modificar el orden de turno. Un sistema de suscripción ofrece tres planes: gratuito (limitado), evento único y premium (suscripción mensual).',
      },
      singers: {
        title: 'Para cantantes',
        description: 'Los cantantes pueden unirse a eventos a través de código QR, agregar canciones a la cola del evento y ver el orden de canciones. Los cantantes pueden participar sin cuenta (modo invitado) o con una cuenta para funciones avanzadas.',
      },
    },
    accounts: {
      title: 'Cuentas de usuario',
      description: 'Para usar KaraoNow, debe crear una cuenta. Usted es responsable de mantener la confidencialidad de sus credenciales.',
      responsibility: 'Usted es responsable de todas las actividades realizadas a través de su cuenta',
      accuracy: 'Acepta proporcionar información precisa y actualizada',
      security: 'Acepta mantener la seguridad de su contraseña',
      notTransferable: 'Su cuenta es personal e intransferible',
    },
    subscription: {
      title: 'Planes de suscripción',
      description: 'KaraoNow ofrece varios planes de suscripción para organizadores:',
      free: 'Plan gratuito: Máximo 4 eventos (todos los estados combinados, excluyendo eventos únicos), 15 canciones por evento',
      oneShot: 'Evento único: Pago único para un evento específico, canciones ilimitadas, período de validez limitado',
      premium: 'Planes de pago con suscripción mensual: Varios planes disponibles con cuotas mensuales de eventos, canciones ilimitadas por evento, renovación automática mensual',
    },
    payment: {
      title: 'Términos de pago',
      description: 'El pago se procesa a través de un procesador de pago seguro de terceros.',
      currency: 'Los pagos se realizan en euros (EUR)',
      noRefund: 'No se reembolsan los servicios ya prestados',
      cancellation: 'Puede cancelar su suscripción en cualquier momento',
      automaticRenewal: 'Las suscripciones mensuales se renuevan automáticamente cada mes a menos que se cancelen',
    },
    conduct: {
      title: 'Conducta del usuario',
      description: 'Acepta usar KaraoNow de manera legal y ética.',
      prohibited: {
        title: 'Acciones prohibidas',
        illegal: 'Cualquier actividad ilegal o violación de leyes aplicables',
        harmful: 'Contenido dañino, ofensivo o difamatorio',
        harassing: 'Acoso, intimidación o discriminación',
        spam: 'Envío de spam u otras comunicaciones no deseadas',
        fraud: 'Intentos de fraude o estafas',
      },
    },
    organizerResponsibilities: {
      title: 'Responsabilidades del organizador',
      description: 'Los organizadores aceptan las siguientes responsabilidades:',
      songSelection: 'Usted es responsable de la legalidad del contenido de las canciones',
      eventManagement: 'Debe gestionar su evento de acuerdo con estos términos',
      dataManagement: 'Usted es responsable de gestionar los datos de los cantantes',
      copyrightCompliance: 'Acepta cumplir con las leyes de derechos de autor para canciones',
    },
    singerRights: {
      title: 'Derechos y responsabilidades del cantante',
      description: 'Los cantantes tienen los siguientes derechos y aceptan las siguientes responsabilidades:',
      participation: 'Puede participar en eventos organizados por otros',
      songSelection: 'Puede agregar canciones a la cola del evento',
      noDuplication: 'No puede agregar la misma canción varias veces',
      respect: 'Acepta respetar las reglas establecidas por el organizador',
    },
    intellectualProperty: {
      title: 'Propiedad intelectual',
      description: 'Todos los derechos de propiedad intelectual de KaraoNow y su contenido (excepto las canciones musicales) pertenecen a KaraoNow. No puede reproducir o usar el contenido sin permiso.',
    },
    liability: {
      title: 'Limitación de responsabilidad',
      description: 'KaraoNow se proporciona "tal cual". No somos responsables de daños directos o indirectos resultantes del uso de la plataforma. Nuestra responsabilidad se limita al máximo que ha pagado por su suscripción en el último mes.',
    },
    termination: {
      title: 'Terminación',
      description: 'KaraoNow se reserva el derecho de suspender o terminar su cuenta en caso de incumplimiento de estos términos. Puede terminar su cuenta en cualquier momento.',
    },
    modifications: {
      title: 'Modificaciones de términos',
      description: 'KaraoNow se reserva el derecho de modificar estos términos en cualquier momento. Las modificaciones entran en vigencia 30 días después de la notificación. Su uso continuado de la plataforma después de este período significa su aceptación de los nuevos términos.',
    },
    jurisdiction: {
      title: 'Jurisdicción',
      description: 'Estos términos se rigen por la ley francesa. Cualquier disputa estará sujeta a los tribunales competentes en Francia.',
    },
    contact: {
      title: 'Contacto',
      description: 'Para cualquier pregunta sobre estos términos, contáctenos:',
      email: 'support@karaonow.com',
    },
  },
}
